// @flow
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, FormControl, FormHelperText, FormLabel, IconButton, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ErrorMessage } from "formik";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { v4 as uuidv4 } from "uuid";
import FormBoxTitle from "../../../../../components/mui/app/formBoxTitle/formBoxTitle";
import {
	edit_issue_add_items,
	edit_issue_delete_items,
	edit_issue_edit_items,
	edit_issue_items_price,
	edit_issue_items_total_value,
	edit_issue_items_value,
	issue_add_items,
	issue_delete_items,
	issue_edit_items,
	issue_items_price, issue_items_total_value,
	issue_items_value
} from "../../../../../helpers/rights/rights";
import {
	formControlDefaults, numericFormatDefaults, textFieldDefaults,
} from "../../../../../helpers/theme/defaultProps";
import useUserData from "../../../../../hooks/user/useUserData";
import PermissionHOC from "../../../../permission/components/permissionHOC/permissionHOC";
import { ISSUES_FORM } from "../../../locales/namespaces";
import ItemAutoComplete from "./itemAutoComplete";

type Props = {};

const Items = (props: Props) => {
	const { t } = useTranslation(ISSUES_FORM)
	const { formik, initialItems, theme, IssueId, isBelowSm } = props
	const { thousandSeparator, decimalSeparator } = useUserData()
	
	const addItem = useCallback(() => {
		formik.setFieldValue('items', [
			...formik.values.items,
			{
				id: uuidv4(),
				ItemDescription: '',
				ItemId: '',
				Qty: 0,
				ItemValue: 0,
				ItemPrice: 0,
			}
		])
	}, [formik])
	
	const removeItem = useCallback((index) => {
		const updatedItems = [...formik.values.items]
		updatedItems.splice(index, 1)
		formik.setFieldValue('items', updatedItems)
	}, [formik])
	
	const totalValue = useMemo(() => (
		formik.values.items?.length > 0
			? formik.values.items.reduce((sum, item) => {
				const qty = item?.Qty || 0;
				const price = item?.ItemPrice || 0;
				return sum + (qty * price);
			}, 0)
			: 0
	), [formik.values.items])
	
	return (
		<Grid
			size={12}
		>
			<FormBoxTitle
				title={t('Items')}
				children={
					<PermissionHOC
						requiredPermissions={[IssueId ? edit_issue_add_items : issue_add_items]}>
						<Button
							onClick={addItem}
							variant={'text'}
							startIcon={<AddIcon/>}
						>
							{t('Add')}
						</Button>
					</PermissionHOC>
				}
			/>
			{formik.values.items?.map((item, itemIndex) => {
				return (
					<Box key={item?.id} sx={{ borderLeft: `4px solid ${theme.palette.info.main}` }}>
						<PermissionHOC
							requiredPermissions={[IssueId ? edit_issue_delete_items : issue_delete_items]}>
							<Grid size={12}>
								<Box sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									background: '#fff5ee',
									paddingLeft: 0.4,
									paddingRight: 0.4
								}}>
									<FormControl {...formControlDefaults}
												 sx={{ display: 'inline' }}>
										<PermissionHOC
											requiredPermissions={[IssueId ? edit_issue_delete_items : issue_delete_items]}>
											<IconButton color="error"
														onClick={() => removeItem(itemIndex)}>
												<ClearIcon/>
											</IconButton>
										</PermissionHOC>
									</FormControl>
								</Box>
							</Grid>
						</PermissionHOC>
						<Grid
							container
							spacing={1}
							sx={{ padding: 0.4, marginBottom: 0.8 }}
						>
							<Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
								<FormControl {...formControlDefaults}>
									<PermissionHOC
										requiredPermissions={[IssueId ? edit_issue_edit_items : issue_edit_items]}>
										<FormLabel>{t('Item')}</FormLabel>
										<ItemAutoComplete
											IssueId={IssueId}
											itemIndex={itemIndex}
											items={initialItems}
											value={item?.ItemId}
											onChange={(evt, newValue, reason) => {
												if (reason === 'clear') {
													formik.setFieldValue(`items[${itemIndex}].ItemId`, '')
												} else {
													formik.setFieldValue(`items[${itemIndex}].ItemId`, newValue)
												}
											}}
											onBlur={formik.handleBlur}
										/>
										<FormHelperText>
											<ErrorMessage name={`items[${itemIndex}].ItemId`}/>
										</FormHelperText>
									</PermissionHOC>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
								<FormControl {...formControlDefaults}>
									<PermissionHOC
										requiredPermissions={[IssueId ? edit_issue_edit_items : issue_edit_items]}>
										<FormLabel>{t('Description')}</FormLabel>
										<TextField
											{...textFieldDefaults}
											name={`items[${itemIndex}].ItemDescription`}
											value={item.ItemDescription}
											onChange={(e) => formik.setFieldValue(`items[${itemIndex}].ItemDescription`, e.target.value)}
											onBlur={formik.handleBlur}
										/>
										<FormHelperText>
											<ErrorMessage
												name={`items[${itemIndex}].ItemDescription`}/>
										</FormHelperText>
									</PermissionHOC>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }}>
								<FormControl {...formControlDefaults}>
									<PermissionHOC
										requiredPermissions={[IssueId ? edit_issue_edit_items : issue_edit_items]}>
										<FormLabel>{t('Quantity')}</FormLabel>
										<NumericFormat
											{...numericFormatDefaults}
											thousandSeparator={thousandSeparator}
											decimalSeparator={decimalSeparator}
											name={`items[${itemIndex}].Qty`}
											value={item.Qty}
											onValueChange={(values, sourceInfo) => {
												const qty = values.floatValue || 0;
												const price = item.ItemPrice || 0;
												const value = qty * price;
												formik.setFieldValue(`items[${itemIndex}]`, {
													...item,
													Qty: qty,
													ItemValue: value
												})
											}}
											onBlur={formik.handleBlur}
										/>
									</PermissionHOC>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }}>
								<FormControl {...formControlDefaults}>
									<PermissionHOC
										requiredPermissions={[IssueId ? edit_issue_items_price : issue_items_price]}>
										<FormLabel>{t('Price')}</FormLabel>
										<NumericFormat
											{...numericFormatDefaults}
											thousandSeparator={thousandSeparator}
											decimalSeparator={decimalSeparator}
											name={`items[${itemIndex}].ItemPrice`}
											value={item.ItemPrice}
											onValueChange={(values, sourceInfo) => {
												const price = values.floatValue || 0;
												const qty = item.Qty || 0;
												const value = qty * price;
												formik.setFieldValue(`items[${itemIndex}]`, {
													...item,
													ItemPrice: price,
													ItemValue: value
												})
											}}
											onBlur={formik.handleBlur}
										/>
									</PermissionHOC>
								</FormControl>
							</Grid>
							<PermissionHOC
								requiredPermissions={[IssueId ? edit_issue_items_value : issue_items_value]}>
								<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }}>
									<FormControl {...formControlDefaults}>
										<FormLabel>{t('Value')}</FormLabel>
										<NumericFormat
											{...numericFormatDefaults}
											thousandSeparator={thousandSeparator}
											decimalSeparator={decimalSeparator}
											name={`items[${itemIndex}].ItemValue`}
											value={item.ItemValue}
											onBlur={formik.handleBlur}
											disabled={true}
										/>
									</FormControl>
								</Grid>
							</PermissionHOC>
						</Grid>
					</Box>
				)
			})}
			{formik.values.items?.length > 0 && (
				<PermissionHOC
					requiredPermissions={[IssueId ? edit_issue_items_total_value : issue_items_total_value]}>
					<Grid
						container
						spacing={isBelowSm ? 0 : 1}
						alignItems={'end'}
						justifyContent={'flex-end'}
					>
						<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }}>
							<FormControl {...formControlDefaults}>
								<FormLabel>{t('Total Value')}</FormLabel>
								<NumericFormat
									{...numericFormatDefaults}
									thousandSeparator={thousandSeparator}
									decimalSeparator={decimalSeparator}
									name={'TotalValue'}
									value={totalValue}
									disabled={true}
								/>
							</FormControl>
						</Grid>
					</Grid>
				</PermissionHOC>
			)}
		</Grid>
	);
};

export default Items